import {
  calculerIndemnites,
  formaterAdresse,
  trimestreDe,
  trimestreDecale,
  type AdresseCoreDto,
  type BaremeDto,
  type DistanceSource,
  type Niveau,
  type PeriodeDto,
  type ReglagesDto,
  type Role,
  type Statut,
  type Trimestre,
} from '@igd/shared';

/**
 * LE MONDE FICTIF du mode démo : une petite entreprise complète, gardée dans
 * le navigateur (localStorage) et jamais envoyée à l'API.
 *
 * POURQUOI PAS UN BAC À SABLE EN BASE, comme Plans d'actions ? Ici, chaque
 * geste écrit dans des journaux EN AJOUT SEUL, conservés cinq ans sans purge
 * possible (CLAUDE.md, règle 4 ; CDC §11) : une démonstration côté serveur y
 * laisserait pour toujours des validations et des exports fictifs. Et le
 * circuit IGD ne se comprend qu'en changeant de rôle (on ne valide jamais sa
 * propre déclaration) : la démo doit pouvoir faire jouer le manager, la RH et
 * l'administrateur à quelqu'un qui n'a aucun de ces rôles.
 *
 * Les RÈGLES, elles, ne sont pas recopiées : le serveur de démo appelle les
 * mêmes fonctions de @igd/shared que l'API (calcul, contrôles, workflow).
 * Les dates sont relatives au jour : la démo ne vieillit pas.
 */

export const VERSION_ETAT = 3;

/* ---------------------------------------------------------------- Types */

export interface UtilisateurDemo {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  matricule: string | null;
  societeId: string | null;
  siteId: string | null;
  managerId: string | null;
  roles: Role[];
  active: boolean;
  adresseDomicile: string | null;
  lastLoginAt: string | null;
}

export interface SocieteDemo {
  id: string;
  code: string;
  nom: string;
  active: boolean;
}

export interface SiteDemo {
  id: string;
  societeId: string;
  nom: string;
  active: boolean;
}

export interface DeclarationDemo {
  id: string;
  reference: string;
  collaborateurId: string;
  periodeId: string | null;
  statut: Statut;
  dateDeplacement: string;
  mission: string;
  client: string;
  adresseDepart: string;
  adressePrestation: string;
  distanceAllerKm: number | null;
  distanceApiKm: number | null;
  distanceSource: DistanceSource;
  nuitee: boolean;
  nbNuitees: number | null;
  hotel: string | null;
  commentaire: string | null;
  baremeId: string | null;
  kmAllerRetour: number;
  kmIndemnises: number;
  heuresIndemnisees: number;
  eligibleIgd: boolean;
  montantIgd: number;
  montantNuitee: number;
  montantTotal: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string | null;
  submittedAt: string | null;
  exportId: string | null;
}

export interface EvenementDemo {
  id: string;
  declarationId: string;
  at: string;
  acteurId: string | null;
  action: string;
  niveau: Niveau | null;
  statutAvant: Statut | null;
  statutApres: Statut | null;
  commentaire: string | null;
  changements: Record<string, [unknown, unknown]> | null;
}

export interface AuditDemo {
  id: string;
  at: string;
  acteurId: string | null;
  action: string;
  objet: string | null;
  objetId: string | null;
  details: Record<string, unknown> | null;
}

export interface ConnexionDemo {
  id: string;
  at: string;
  acteurId: string;
  succes: boolean;
}

export interface ExportDemo {
  id: string;
  periodeId: string;
  format: string;
  nbLignes: number;
  montantTotal: number;
  createdAt: string;
  createdBy: string;
}

/** Le carnet d'adresses `core`, tel que l'API le lirait (clients, sociétés du groupe, salariés). */
export interface ClientCoreDemo {
  id: string;
  numeroClient: string;
  raisonSociale: string;
  interne: boolean;
  email: string | null;
  telFixe: string | null;
  adresse: AdresseCoreDto | null;
  sites: Array<{ id: string; nom: string; numeroAgiris: string | null }>;
}

export interface SalarieCoreDemo {
  id: string;
  numeroClient: string;
  siteId: string | null;
  nom: string;
  prenom: string;
  email: string;
  telMobile: string | null;
  fonction: string | null;
}

export interface EtatDemo {
  version: number;
  compteur: number;
  utilisateurs: UtilisateurDemo[];
  societes: SocieteDemo[];
  sites: SiteDemo[];
  baremes: BaremeDto[];
  periodes: PeriodeDto[];
  reglages: ReglagesDto;
  declarations: DeclarationDemo[];
  evenements: EvenementDemo[];
  audit: AuditDemo[];
  connexions: ConnexionDemo[];
  exports: ExportDemo[];
  clientsCore: ClientCoreDemo[];
  salariesCore: SalarieCoreDemo[];
}

/* ---------------------------------------------------------- Personnages */

/** Les rôles qu'on peut ENDOSSER depuis le bandeau : un par étape du circuit. */
export const PERSONAS = {
  collaborateur: '00000000-0000-4000-8000-00000000d0c1',
  manager: '00000000-0000-4000-8000-00000000d0a1',
  rh: '00000000-0000-4000-8000-00000000d0b1',
  admin: '00000000-0000-4000-8000-00000000d0e1',
} as const;
export type PersonaId = keyof typeof PERSONAS;

export const PERSONA_LIBELLES: Record<PersonaId, { nom: string; role: string; resume: string }> = {
  collaborateur: { nom: 'Claire DURAND', role: 'Collaboratrice', resume: 'déclare ses déplacements' },
  manager: { nom: 'Marc MARTIN', role: 'Manager', resume: 'valide son équipe (niveau 1)' },
  rh: { nom: 'Rachel PAIE', role: 'RH / Paie', resume: 'valide (niveau 2) et exporte la paie' },
  admin: { nom: 'Alice ADMIN', role: 'Administratrice', resume: 'comptes, barèmes, périodes' },
};

const HUGO = '00000000-0000-4000-8000-00000000d0c2';
const INES = '00000000-0000-4000-8000-00000000d0c3';

const SOC_ALT = '00000000-0000-4000-8000-00000000a0a1';
const SOC_ITS = '00000000-0000-4000-8000-00000000a0a2';
const SITE_LAVAL = '00000000-0000-4000-8000-00000000b0b1';
const SITE_NANTES = '00000000-0000-4000-8000-00000000b0b2';
const SITE_ITS_LAVAL = '00000000-0000-4000-8000-00000000b0b3';

/* ---------------------------------------------------------------- Dates */

/** Aujourd'hui à Paris, AAAA-MM-JJ (comme l'API : le poste peut être mal réglé). */
export function aujourdhui(): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date());
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Décale une date AAAA-MM-JJ d'un nombre de jours (calcul en UTC : pas de piège d'heure d'été). */
export function decalerJours(date: string, jours: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + jours);
  return iso(d);
}


/** Un horodatage ISO pour une date donnée, à une heure « de bureau ». */
function horodatage(date: string, heure = 9): string {
  return `${date}T${String(heure).padStart(2, '0')}:${String((heure * 7) % 60).padStart(2, '0')}:00.000Z`;
}

/* ---------------------------------------------------------- Construction */

/** Le monde de départ — rejoué par « Réinitialiser ». */
export function etatInitial(): EtatDemo {
  const jour = aujourdhui();
  // Périodes TRIMESTRIELLES, comme en production : les deux trimestres passés et le trimestre en cours.
  const m0 = trimestreDe(jour);
  const m1 = trimestreDecale(m0, -1);
  const m2 = trimestreDecale(m0, -2);
  const debutAnnee = `${jour.slice(0, 4)}-01-01`;
  const anneePassee = `${Number(jour.slice(0, 4)) - 1}-01-01`;

  const etat: EtatDemo = {
    version: VERSION_ETAT,
    compteur: 120,
    utilisateurs: [
      u(PERSONAS.admin, 'alice.admin@demo.igd', 'ADMIN', 'Alice', 'D0001', SOC_ITS, SITE_ITS_LAVAL, null, ['collaborateur', 'admin'], null),
      u(PERSONAS.rh, 'rachel.paie@demo.igd', 'PAIE', 'Rachel', 'D0002', SOC_ALT, SITE_LAVAL, null, ['collaborateur', 'rh'], '4 rue du Val de Mayenne, 53000 Laval'),
      u(PERSONAS.manager, 'marc.martin@demo.igd', 'MARTIN', 'Marc', 'D0003', SOC_ALT, SITE_LAVAL, null, ['collaborateur', 'manager'], '12 rue de Paris, 53000 Laval'),
      u(PERSONAS.collaborateur, 'claire.durand@demo.igd', 'DURAND', 'Claire', 'D0004', SOC_ALT, SITE_LAVAL, PERSONAS.manager, ['collaborateur'], '5 place du 11 Novembre, 53000 Laval'),
      u(HUGO, 'hugo.leroy@demo.igd', 'LEROY', 'Hugo', 'D0005', SOC_ALT, SITE_NANTES, PERSONAS.manager, ['collaborateur'], '1 quai de la Fosse, 44000 Nantes'),
      // SANS MATRICULE, exprès : sa déclaration validée bloque l'export paie
      // tant que l'administratrice ne l'a pas complété — un effet de bord à voir.
      u(INES, 'ines.garnier@demo.igd', 'GARNIER', 'Inès', null, SOC_ALT, SITE_NANTES, PERSONAS.manager, ['collaborateur'], null),
    ],
    societes: [
      { id: SOC_ALT, code: 'ALT', nom: 'Altonéo', active: true },
      { id: SOC_ITS, code: 'ITS', nom: 'IT Solutions', active: true },
    ],
    sites: [
      { id: SITE_LAVAL, societeId: SOC_ALT, nom: 'Laval', active: true },
      { id: SITE_NANTES, societeId: SOC_ALT, nom: 'Nantes', active: true },
      { id: SITE_ITS_LAVAL, societeId: SOC_ITS, nom: 'Laval', active: true },
    ],
    baremes: [
      bareme('00000000-0000-4000-8000-0000000bae02', debutAnnee, 12.02, 'Revalorisation du SMIC au 1er janvier'),
      bareme('00000000-0000-4000-8000-0000000bae01', anneePassee, 11.88, 'Barème initial (CDC)'),
    ],
    periodes: [
      periode('00000000-0000-4000-8000-0000000fe002', m2, 'cloturee'),
      periode('00000000-0000-4000-8000-0000000fe001', m1, 'cloturee'),
      periode('00000000-0000-4000-8000-0000000fe000', m0, 'ouverte'),
    ],
    reglages: { validationRhActive: true, rappelFinPeriodeJours: 7 },
    declarations: [],
    evenements: [],
    audit: [],
    connexions: [],
    exports: [],
    clientsCore: CLIENTS_CORE,
    salariesCore: SALARIES_CORE,
  };

  const [pM2, pM1, pM0] = [etat.periodes[0]!, etat.periodes[1]!, etat.periodes[2]!];
  /** Un jour du trimestre courant, sans jamais dépasser aujourd'hui (une date future serait refusée). */
  const ceMois = (n: number): string => {
    const d = decalerJours(jour, -n);
    return d < pM0.dateDebut ? pM0.dateDebut : d;
  };
  const dansMois = (p: PeriodeDto, n: number): string => decalerJours(p.dateDebut, n);

  const E = PERSONAS;
  const DOMICILE_CLAIRE = '5 place du 11 Novembre, 53000 Laval';
  const DOMICILE_HUGO = '1 quai de la Fosse, 44000 Nantes';
  const AGENCE_LAVAL = '12 rue de Paris, 53000 Laval';

  // --- Trimestre T-2 : payé (exporté) -----------------------------------------
  const exportM2 = '00000000-0000-4000-8000-00000000e0e1';
  ajouter(etat, E.collaborateur, dansMois(pM2, 3), 'exportee', { client: 'Boulangerie Durand', adresse: '3 boulevard de la Liberté, 44000 Nantes', depart: DOMICILE_CLAIRE, km: 132, mission: 'Audit de caisse' }, { valideur: E.manager, rh: E.rh, exportId: exportM2 });
  ajouter(etat, E.collaborateur, dansMois(pM2, 11), 'exportee', { client: 'Cave des Coteaux', adresse: '8 place du Ralliement, 49100 Angers', depart: DOMICILE_CLAIRE, km: 78, mission: 'Inventaire annuel', nuits: 1, hotel: 'Hôtel du Mail' }, { valideur: E.manager, rh: E.rh, exportId: exportM2 });
  ajouter(etat, HUGO, dansMois(pM2, 7), 'exportee', { client: 'Transports Mayennais', adresse: '2 rue des Docks, 35000 Rennes', depart: DOMICILE_HUGO, km: 110, mission: 'Paramétrage paie' }, { valideur: E.manager, rh: E.rh, exportId: exportM2 });
  etat.exports.push({ id: exportM2, periodeId: pM2.id, format: 'xlsx', nbLignes: 2, montantTotal: 0, createdAt: horodatage(decalerJours(pM2.dateFin, 4), 15), createdBy: E.rh });

  // --- Trimestre T-1 : clôturé, validé, PRÊT à exporter -------------------------
  ajouter(etat, E.collaborateur, dansMois(pM1, 2), 'validee', { client: 'Boulangerie Durand', adresse: '3 boulevard de la Liberté, 44000 Nantes', depart: DOMICILE_CLAIRE, km: 132, mission: 'Clôture mensuelle' }, { valideur: E.manager, rh: E.rh });
  ajouter(etat, E.collaborateur, dansMois(pM1, 16), 'validee', { client: 'Brasserie de Bruxelles', adresse: 'Rue Neuve 1, 1000 Bruxelles, Belgique', depart: DOMICILE_CLAIRE, km: 680, mission: 'Due diligence', nuits: 2, hotel: 'Hôtel Métropole' }, { valideur: E.manager, rh: E.rh });
  ajouter(etat, HUGO, dansMois(pM1, 9), 'validee', { client: 'Garage Central', adresse: '15 avenue de Bretagne, 72000 Le Mans', depart: DOMICILE_HUGO, km: 184, mission: 'Formation logiciel' }, { valideur: E.manager, rh: E.rh });
  ajouter(etat, E.rh, dansMois(pM1, 12), 'validee', { client: 'IT Solutions', adresse: '2 rue des Docks, 35000 Rennes', depart: '4 rue du Val de Mayenne, 53000 Laval', km: 75, mission: 'Séminaire paie' }, { valideur: E.admin, rh: null });

  // --- Trimestre courant : tout le cycle de vie, à faire avancer ----------------
  ajouter(etat, E.collaborateur, ceMois(1), 'brouillon', { client: 'Cave des Coteaux', adresse: '', depart: DOMICILE_CLAIRE, km: null, mission: 'Préparation inventaire' }, {});
  ajouter(etat, E.collaborateur, ceMois(2), 'a_valider', { client: 'Transports Mayennais', adresse: '2 rue des Docks, 35000 Rennes', depart: DOMICILE_CLAIRE, km: 76, mission: 'Révision des comptes', nuits: 1, hotel: 'Ibis Rennes Centre' }, {});
  ajouter(etat, E.collaborateur, ceMois(4), 'a_corriger', { client: 'Garage Central', adresse: '15 avenue de Bretagne, 72000 Le Mans', depart: DOMICILE_CLAIRE, km: 140, mission: 'Bilan intermédiaire' }, { renvoi: 'La distance me paraît élevée : Laval → Le Mans fait environ 90 km. Pouvez-vous vérifier ?' });
  ajouter(etat, E.collaborateur, ceMois(6), 'validee_manager', { client: 'Boulangerie Durand', adresse: '3 boulevard de la Liberté, 44000 Nantes', depart: DOMICILE_CLAIRE, km: 132, mission: 'Situation trimestrielle' }, { valideur: E.manager });
  ajouter(etat, E.collaborateur, ceMois(8), 'validee', { client: 'Brasserie de Bruxelles', adresse: 'Chaussée de Louvain 90, 1210 Bruxelles, Belgique', depart: DOMICILE_CLAIRE, km: 684, mission: 'Revue des stocks', nuits: 1, hotel: 'Hôtel Métropole' }, { valideur: E.manager, rh: E.rh });
  ajouter(etat, E.collaborateur, ceMois(9), 'refusee', { client: 'Agence de Laval', adresse: AGENCE_LAVAL, depart: DOMICILE_CLAIRE, km: 2, mission: 'Réunion d’équipe' }, { refus: 'Réunion à l’agence de rattachement : ce n’est pas un grand déplacement.' });
  ajouter(etat, HUGO, ceMois(3), 'a_valider', { client: 'Cave des Coteaux', adresse: '8 place du Ralliement, 49100 Angers', depart: DOMICILE_HUGO, km: 92, mission: 'Inventaire annuel', nuits: 2, hotel: 'Hôtel du Mail' }, {});
  ajouter(etat, HUGO, ceMois(5), 'validee_manager', { client: 'Garage Central', adresse: '15 avenue de Bretagne, 72000 Le Mans', depart: DOMICILE_HUGO, km: 184, mission: 'Formation logiciel' }, { valideur: E.manager });
  ajouter(etat, INES, ceMois(7), 'validee', { client: 'Transports Mayennais', adresse: '10 rue Magenta, 53000 Laval', depart: '6 rue de Strasbourg, 44000 Nantes', km: 135, mission: 'Accompagnement fiscal' }, { valideur: E.manager, rh: E.rh });
  // Marc n'a pas de manager : la RH tient le niveau 1 « en secours ».
  ajouter(etat, E.manager, ceMois(2), 'a_valider', { client: 'Brasserie de Bruxelles', adresse: 'Rue Neuve 1, 1000 Bruxelles, Belgique', depart: AGENCE_LAVAL, km: 680, mission: 'Réunion de lancement', nuits: 1, hotel: 'Hôtel Métropole' }, {});

  // Le montant de l'export M-2 se déduit des déclarations qui y sont rattachées.
  etat.exports[0]!.montantTotal = etat.declarations
    .filter((d) => d.exportId === exportM2)
    .reduce((s, d) => Math.round((s + d.montantTotal) * 100) / 100, 0);

  etat.connexions.push({ id: id(), at: new Date().toISOString(), acteurId: PERSONAS.collaborateur, succes: true });
  return etat;
}

function u(
  idU: string,
  email: string,
  nom: string,
  prenom: string,
  matricule: string | null,
  societeId: string | null,
  siteId: string | null,
  managerId: string | null,
  roles: Role[],
  adresseDomicile: string | null,
): UtilisateurDemo {
  return { id: idU, email, nom, prenom, matricule, societeId, siteId, managerId, roles, active: true, adresseDomicile, lastLoginAt: null };
}

function bareme(idB: string, dateEffet: string, smic: number, commentaire: string): BaremeDto {
  return {
    id: idB,
    dateEffet,
    seuilKmAllerRetour: 80,
    diviseurKmHeure: 120,
    smicHoraire: smic,
    coefficientSmic: 2,
    montantNuitee: 30,
    commentaire,
    createdAt: horodatage(dateEffet, 8),
    createdBy: 'Alice ADMIN',
  };
}

function periode(idP: string, t: Trimestre, statut: 'ouverte' | 'cloturee'): PeriodeDto {
  return {
    id: idP,
    libelle: t.libelle,
    dateDebut: t.dateDebut,
    dateFin: t.dateFin,
    dateLimite: t.dateLimite,
    dateLimiteValidation: t.dateLimiteValidation,
    statut,
  };
}

/** Identifiant neuf (uuid v4). */
export function id(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

export function nouvelleReference(etat: EtatDemo): string {
  etat.compteur += 1;
  return `IGD-${String(etat.compteur).padStart(6, '0')}`;
}

export function baremeApplicable(etat: EtatDemo, date: string): BaremeDto | null {
  return [...etat.baremes].sort((a, b) => b.dateEffet.localeCompare(a.dateEffet)).find((b) => b.dateEffet <= date) ?? null;
}

export function periodeCouvrant(etat: EtatDemo, date: string): PeriodeDto | null {
  return etat.periodes.find((p) => date >= p.dateDebut && date <= p.dateFin) ?? null;
}

interface Saisie {
  client: string;
  adresse: string;
  depart: string;
  km: number | null;
  mission: string;
  nuits?: number;
  hotel?: string;
}

interface Histoire {
  valideur?: string;
  rh?: string | null;
  renvoi?: string;
  refus?: string;
  exportId?: string;
}

/** Ajoute une déclaration ET l'historique qui l'a amenée à son statut. */
function ajouter(etat: EtatDemo, collaborateurId: string, date: string, statut: Statut, s: Saisie, h: Histoire): void {
  const b = baremeApplicable(etat, date);
  const calcul = b
    ? calculerIndemnites({ distanceAllerKm: s.km ?? 0, nuitee: !!s.nuits, nbNuitees: s.nuits ?? 0 }, b)
    : { kmAllerRetour: 0, kmIndemnises: 0, heuresIndemnisees: 0, eligibleIgd: false, montantIgd: 0, montantNuitee: 0, montantTotal: 0 };
  const cree = horodatage(date, 18);
  const d: DeclarationDemo = {
    id: id(),
    reference: nouvelleReference(etat),
    collaborateurId,
    periodeId: periodeCouvrant(etat, date)?.id ?? null,
    statut,
    dateDeplacement: date,
    mission: s.mission,
    client: s.client,
    adresseDepart: s.depart,
    adressePrestation: s.adresse,
    distanceAllerKm: s.km,
    distanceApiKm: s.km,
    distanceSource: s.km == null ? 'manuelle' : 'api',
    nuitee: !!s.nuits,
    nbNuitees: s.nuits ?? null,
    hotel: s.hotel ?? null,
    commentaire: null,
    baremeId: b?.id ?? null,
    ...calcul,
    createdAt: cree,
    createdBy: collaborateurId,
    updatedAt: cree,
    updatedBy: collaborateurId,
    submittedAt: statut === 'brouillon' ? null : horodatage(decalerJours(date, 1), 8),
    exportId: h.exportId ?? null,
  };
  etat.declarations.push(d);

  const ev = (jours: number, heure: number, acteurId: string, action: string, avant: Statut | null, apres: Statut, extra: Partial<EvenementDemo> = {}): void => {
    etat.evenements.push({
      id: id(),
      declarationId: d.id,
      at: horodatage(decalerJours(date, jours), heure),
      acteurId,
      action,
      niveau: null,
      statutAvant: avant,
      statutApres: apres,
      commentaire: null,
      changements: null,
      ...extra,
    });
  };
  ev(0, 18, collaborateurId, 'creer', null, 'brouillon');
  if (statut === 'brouillon') return;
  ev(1, 8, collaborateurId, 'soumettre', 'brouillon', 'a_valider');
  if (h.renvoi) {
    ev(1, 14, PERSONAS.manager, 'renvoyer', 'a_valider', 'a_corriger', { niveau: 'manager', commentaire: h.renvoi });
    return;
  }
  if (h.refus) {
    ev(1, 14, PERSONAS.manager, 'refuser', 'a_valider', 'refusee', { niveau: 'manager', commentaire: h.refus });
    return;
  }
  if (!h.valideur) return;
  ev(1, 15, h.valideur, 'valider', 'a_valider', 'validee_manager', { niveau: 'manager' });
  if (statut === 'validee_manager') return;
  // RH qui déclare : pas de second niveau par elle-même — la démo la fait
  // valider directement (l'admin tient le niveau 1 en secours, sans RH derrière).
  if (h.rh) ev(2, 10, h.rh, 'valider', 'validee_manager', 'validee', { niveau: 'rh' });
  else etat.evenements[etat.evenements.length - 1]!.statutApres = 'validee';
  if (statut === 'exportee') ev(30, 16, PERSONAS.rh, 'exporter', 'validee', 'exportee', { commentaire: `Export paie ${h.exportId}` });
}

/* ------------------------------------------------------ Carnet d'adresses */

const adr = (rue: string | null, codePostal: string | null, ville: string | null, pays = 'France'): AdresseCoreDto => ({ rue, codePostal, ville, pays });

const CLIENTS_CORE: ClientCoreDemo[] = [
  // Sociétés du GROUPE (`interne`) : ce que l'administration propose.
  { id: 'c-alt', numeroClient: '03', raisonSociale: 'Altonéo', interne: true, email: 'contact@altoneo.demo', telFixe: null, adresse: adr('12 rue de Paris', '53000', 'Laval'),
    sites: [{ id: 's-alt-lav', nom: 'Laval', numeroAgiris: '03-LAV' }, { id: 's-alt-nan', nom: 'Nantes', numeroAgiris: '03-NAN' }, { id: 's-alt-ang', nom: 'Angers', numeroAgiris: '03-ANG' }] },
  { id: 'c-its', numeroClient: '04', raisonSociale: 'IT Solutions', interne: true, email: 'contact@it-solutions.demo', telFixe: null, adresse: adr('12 rue de Paris', '53000', 'Laval'),
    sites: [{ id: 's-its-lav', nom: 'Laval', numeroAgiris: null }, { id: 's-its-ren', nom: 'Rennes', numeroAgiris: null }] },
  { id: 'c-altc', numeroClient: '05', raisonSociale: 'Altonéo Conseil', interne: true, email: null, telFixe: null, adresse: adr('8 place du Ralliement', '49100', 'Angers'),
    sites: [{ id: 's-altc-ang', nom: 'Angers', numeroAgiris: null }, { id: 's-altc-lm', nom: 'Le Mans', numeroAgiris: null }] },
  // CLIENTS : ce que la saisie d'une déclaration propose.
  { id: 'c-dur', numeroClient: '010001', raisonSociale: 'Boulangerie Durand', interne: false, email: 'contact@durand.demo', telFixe: '0240000001', adresse: adr('3 boulevard de la Liberté', '44000', 'Nantes'), sites: [] },
  { id: 'c-bxl', numeroClient: '010002', raisonSociale: 'Brasserie de Bruxelles', interne: false, email: null, telFixe: null, adresse: adr('Rue Neuve 1', '1000', 'Bruxelles', 'Belgique'), sites: [] },
  { id: 'c-tm', numeroClient: '010003', raisonSociale: 'Transports Mayennais', interne: false, email: null, telFixe: '0243000003', adresse: adr('10 rue Magenta', '53000', 'Laval'), sites: [] },
  { id: 'c-cav', numeroClient: '010004', raisonSociale: 'Cave des Coteaux', interne: false, email: null, telFixe: null, adresse: adr('8 place du Ralliement', '49100', 'Angers'), sites: [] },
  { id: 'c-gar', numeroClient: '010005', raisonSociale: 'Garage Central', interne: false, email: 'atelier@garage-central.demo', telFixe: null, adresse: adr('15 avenue de Bretagne', '72000', 'Le Mans'), sites: [] },
  { id: 'c-pha', numeroClient: '010006', raisonSociale: 'Pharmacie du Port', interne: false, email: null, telFixe: null, adresse: adr('4 quai Duguay-Trouin', '35400', 'Saint-Malo'), sites: [] },
  { id: 'c-hor', numeroClient: '010007', raisonSociale: 'Horticulture Leblanc', interne: false, email: null, telFixe: null, adresse: null, sites: [] },
];

const SALARIES_CORE: SalarieCoreDemo[] = [
  { id: 'k-claire', numeroClient: '03', siteId: 's-alt-lav', nom: 'DURAND', prenom: 'Claire', email: 'claire.durand@demo.igd', telMobile: null, fonction: 'Comptable' },
  { id: 'k-hugo', numeroClient: '03', siteId: 's-alt-nan', nom: 'LEROY', prenom: 'Hugo', email: 'hugo.leroy@demo.igd', telMobile: null, fonction: 'Consultant paie' },
  { id: 'k-thomas', numeroClient: '03', siteId: 's-alt-ang', nom: 'PETIT', prenom: 'Thomas', email: 'thomas.petit@demo.igd', telMobile: '0600000010', fonction: 'Auditeur' },
  { id: 'k-lea', numeroClient: '03', siteId: 's-alt-nan', nom: 'ROUX', prenom: 'Léa', email: 'lea.roux@demo.igd', telMobile: null, fonction: 'Assistante comptable' },
  { id: 'k-sophie', numeroClient: '04', siteId: 's-its-ren', nom: 'BERNARD', prenom: 'Sophie', email: 'sophie.bernard@demo.igd', telMobile: '0600000013', fonction: 'Technicienne réseau' },
  { id: 'k-julien', numeroClient: '05', siteId: 's-altc-lm', nom: 'FAURE', prenom: 'Julien', email: 'julien.faure@demo.igd', telMobile: null, fonction: 'Consultant' },
];

/** Ligne d'adresse d'un client du carnet. */
export function adresseClient(c: ClientCoreDemo): string | null {
  return formaterAdresse(c.adresse);
}

/* ---------------------------------------------------------- Persistance */

const CLE = 'igd.demo.etat';

export function chargerEtat(): EtatDemo {
  try {
    const brut = localStorage.getItem(CLE);
    if (brut) {
      const e = JSON.parse(brut) as EtatDemo;
      if (e?.version === VERSION_ETAT && Array.isArray(e.declarations)) return e;
    }
  } catch {
    /* stockage indisponible ou corrompu : on repart du monde initial */
  }
  const e = etatInitial();
  sauverEtat(e);
  return e;
}

export function sauverEtat(e: EtatDemo): void {
  try {
    localStorage.setItem(CLE, JSON.stringify(e));
  } catch {
    // Navigation privée stricte : la démo marche, elle ne survivra pas au rechargement.
  }
}

export function effacerEtat(): void {
  try {
    localStorage.removeItem(CLE);
  } catch {
    /* rien à effacer */
  }
}
